// Single source of truth for the Projects page, case-study pages and footer links.
// Case-study screenshots live in public/images/case-studies/<slug>/<file>; until a file
// exists, ScreenshotFrame renders a labelled placeholder.

const screenshot = (title, file, label) => ({ file, label, alt: `${title}: ${label}` });

const STRIKE = 'Strike After Dark';
const ZOEY = 'Zoey — Digital Concierge for Concier';
const TRADING = 'Automated Trading & Market Intelligence Platform';

export const caseStudies = [
  {
    slug: 'trading-platform',
    title: TRADING,
    status: { light: '●', tone: 'live', label: 'In production' },
    summary:
      'A production 0DTE options trading and market-intelligence platform that identifies, evaluates, executes, and reports short-term opportunities across SPY, QQQ, and IWM.',
    // TODO(Maurice): "My role" copy for the trading platform — not in the spec, section hidden until provided.
    role: null,
    architecture: [
      'Market Data',
      'Strategy Detection',
      'Risk Validation',
      'Order Execution',
      'Position Management',
      'Reporting',
    ],
    architectureFeatured: true,
    flagship: {
      label: 'Flagship 01 / Trading',
      badge: { light: '●', tone: 'live', label: 'In production' },
      line: 'A production 0DTE options platform that finds, risk-checks, executes and reports trades across SPY, QQQ and IWM.',
      facts: [
        { label: 'Strategies', value: 'ORB · Premarket Breakout · EMA/VWAP' },
        { label: 'Risk controls', value: '11 automated guards' },
        { label: 'Brokers', value: 'Tastytrade execution · Tradier data' },
      ],
    },
    architectureNote:
      'The dashboard represents existing system state; reporting never modifies trading decisions or execution.',
    screenshots: [
      screenshot(TRADING, 'performance-dashboard.jpg', 'Performance dashboard'),
      screenshot(TRADING, 'calendar-pnl-view.jpg', 'Calendar P&L view'),
      screenshot(TRADING, 'strategy-breakout-event-log.jpg', 'Strategy & breakout event log'),
      screenshot(TRADING, 'risk-controls-account-state.png', 'Risk controls / account state'),
    ],
    stack: [
      {
        group: 'Frontend',
        items: [
          'React',
          'Vite',
          'JavaScript/JSX',
          'Responsive dashboard',
          'Data visualization',
          'Interactive performance charts',
          'Real-time strategy and account monitoring',
        ],
      },
      {
        group: 'Backend',
        items: [
          'Node.js',
          'REST APIs',
          'Scheduled market monitoring',
          'Strategy orchestration',
          'Account and position reconciliation',
          'Risk-management services',
        ],
      },
      {
        group: 'Data',
        items: [
          'PostgreSQL',
          'Supabase',
          'Structured trade records',
          'Historical trades',
          'Performance analytics',
          'Strategy/event tracking',
        ],
      },
      {
        group: 'Brokerage & market data',
        items: [
          'Tastytrade API (live options execution, account data)',
          'Tradier API (market data, polling)',
          'DXLink market-data infrastructure',
          'Automated order management',
          'Broker reconciliation',
        ],
      },
      {
        group: 'Infrastructure',
        items: [
          'Railway (backend)',
          'Vercel (frontend)',
          'GitHub',
          'Environment-isolated production and pre-production',
        ],
      },
    ],
    built: [
      {
        heading: 'Strategies',
        items: [
          {
            title: 'Opening Range Breakout',
            text: 'Range from the first three 5-minute bars; requires a 5-minute close through the level and a subsequent hold; controlled re-entry.',
          },
          {
            title: 'Premarket Breakout',
            text: 'Levels from the 4:00–9:30 a.m. session; same close-through + hold confirmation; spread and option-price quality filters.',
          },
          {
            title: 'EMA/VWAP',
            text: 'Moving-average and VWAP structure combined with price location and confirmation.',
          },
        ],
      },
      {
        heading: 'Risk controls',
        items: [
          'Same-session loss protection',
          'Direction/ticker loss locks',
          'Opening-range level-spent protection',
          'Time-based trade restrictions',
          'Contract-price floors',
          'Spread-quality filtering',
          'Position limits',
          'Daily loss controls',
          'Market-hours enforcement',
          'Broker position reconciliation',
          'Automated flattening/time stops',
        ].map((text) => ({ text })),
      },
      {
        heading: 'Reporting',
        items: [
          'Live account budget',
          'Available capital',
          'Daily and cumulative P&L',
          'Strategy performance',
          'Trade/opportunity tracking',
          'Ticker-level activity',
          'Breakout/event logs',
          'Calendar performance view',
          'Historical trade analysis',
        ].map((text) => ({ text })),
      },
    ],
    results: null,
    skills: [
      'Full-stack architecture',
      'FinTech',
      'Algorithmic trading systems',
      'Options-market mechanics',
      'API integration',
      'REST API development',
      'Database architecture',
      'Real-time data processing',
      'Automated order execution',
      'Risk-engine design',
      'Trading-system observability',
      'Production deployment',
      'Environment isolation',
      'Debugging distributed systems',
      'Broker reconciliation',
      'Data integrity and state management',
    ],
  },
  {
    slug: 'strike-after-dark',
    title: STRIKE,
    status: { light: '●', tone: 'live', label: 'Live' },
    summary:
      'Full-stack event ticketing and marketing platform for a nightlife brand. Tiered ticket sales, Stripe Checkout + Connect payouts, QR ticket generation, door scan/check-in, purchaser CRM in Supabase, and automated confirmation emails via Klaviyo — a React marketing site plus static checkout flows on Vercel serverless.',
    // TODO(Maurice): "My role" copy for Strike After Dark — not in the spec, section hidden until provided.
    role: null,
    architecture: [
      'React brand site',
      'Static ticket checkout',
      'Stripe',
      'Webhook/fulfill API',
      'Supabase tickets + Klaviyo email',
      'QR scan at the door',
    ],
    moneyFlow: [
      'Guest buys ticket',
      'Stripe Checkout',
      'Platform fee to me',
      'Payout to venue',
      'QR emailed (Klaviyo)',
      'Scanned at the door',
    ],
    flagship: {
      label: 'Flagship 02 / Monetization',
      badge: { light: '●', tone: 'live', label: 'Live' },
      line: 'A full ticketing business in one build: tiered tickets, Stripe Connect payouts, QR entry at the door, and a platform fee on every sale.',
      facts: [
        { label: 'Payments', value: 'Stripe Checkout · Connect · Tax' },
        { label: 'Data', value: 'Supabase · RLS · idempotent writes' },
        { label: 'Entry', value: 'QR generation + checksum scan' },
      ],
    },
    screenshots: [
      screenshot(STRIKE, 'ticketing-checkout.jpg', 'Ticketing checkout'),
      screenshot(STRIKE, 'door-scanner-check-in.png', 'Door scanner / check-in'),
      screenshot(STRIKE, 'purchaser-crm-supabase.png', 'Purchaser CRM (Supabase)'),
      screenshot(STRIKE, 'vip-night-out-pass.png', 'VIP Night Out Pass'),
    ],
    stack: [
      {
        group: 'Frontend',
        items: [
          'React 18',
          'Vite 6 marketing SPA (homepage, gallery, experiences, FAQ, VIP pass)',
          'Static HTML/JS ticketing funnel (/tickets → payment → success → scan)',
          'Custom CSS design system (dark gold theme, CSS variables)',
          'html-to-image + jsPDF export for the VIP Night Out Pass',
        ],
      },
      {
        group: 'Backend',
        items: [
          'Vercel Serverless Functions (Node.js)',
          'REST endpoints for payments, webhooks, tickets, reservations, subscriptions',
        ],
      },
      {
        group: 'Payments',
        items: [
          'Stripe Checkout Sessions',
          'Stripe Connect destination charges + platform application fees',
          'Stripe Tax',
          'Webhook signature verification',
          'Per-event test vs live mode',
          'Success-page fulfillment fallback if webhooks fail',
        ],
      },
      {
        group: 'Data',
        items: [
          'Supabase / PostgreSQL (tickets, purchasers, customer scoring, lounge/pool reservations, vendors)',
          'RLS policies',
          'RPC for idempotent purchase recording',
          'Staging table isolation',
        ],
      },
      {
        group: 'Email / CRM',
        items: ['Klaviyo Events API (ticket.purchased → transactional flow with QR)'],
      },
      {
        group: 'Security / Ops',
        items: [
          'Cloudflare Turnstile on live checkout',
          'Test/live webhook signing secrets',
          'QR checksum validation for door entry',
          'Feature flags',
          'Env-based config across local/preview/production',
        ],
      },
      {
        group: 'Deploy',
        items: ['Vercel (SPA rewrites + static ticketing routes + serverless APIs)', 'Custom domain'],
      },
    ],
    // TODO(Maurice): "What I built / designed" list for Strike After Dark — not in the spec, section hidden until provided.
    built: null,
    results: null,
    skills: [
      'Full-stack (React SPA + serverless APIs + static commerce pages)',
      'Payments (Checkout, Connect marketplace fees, webhooks, tax, test/live ops)',
      'Databases (schema design, RLS, RPCs, idempotent writes)',
      'Integrations (Klaviyo, Turnstile)',
      'Product features (multi-tier ticketing, QR generation/validation, door scanner, table reservations, customer scoring)',
      'Reliability (webhook + client fulfill dual path, idempotency, staging isolation)',
      'DevOps (Vercel deploys, env management, production debugging via logs)',
      'UI/UX (custom branded CSS, responsive event marketing, gated sections)',
    ],
  },
  {
    slug: 'zoey',
    title: ZOEY,
    status: { light: '◆', tone: 'muted', label: 'In private testing' },
    summary:
      "An AI-driven digital concierge built into Concier, a hospitality and events platform for Central Florida. Zoey learns each guest's preferences over time and plans a complete night out — dinner, drinks, events — instead of returning search results.",
    role:
      'Product owner and technical product lead. I wrote the product vision, made the architecture decisions, and ran a three-layer workflow: me (decisions and approvals), Claude (product management and specs), Claude Code and Cursor (implementation). I reviewed findings, caught gaps, and approved changes before anything reached production.',
    architecture: [
      'Server-rendered Concier site',
      'Zoey API (/v1, JWT token forwarded server to server)',
      'Supabase Postgres (public zoe schema + private zoe_private schema, RLS)',
    ],
    alsoBuilding: {
      title: 'Zoey — AI Digital Concierge for Concier',
      badge: { light: '◆', tone: 'muted', label: 'Private testing' },
      line: "Learns each guest's taste and plans a complete night out. Onboarding cut from 8 screens to 5; median API speedups up to 32% (measured).",
    },
    screenshots: [
      screenshot(ZOEY, 'guest-onboarding.png', 'Guest onboarding'),
      screenshot(ZOEY, 'night-out-plan-multi-stop.png', 'Night-out plan (multi-stop)'),
      screenshot(ZOEY, 'guest-understanding-level.png', 'Guest understanding level'),
    ],
    stack: [
      {
        group: 'Frontend',
        items: [
          'Node.js server on built-in node:http (no framework)',
          'Server-rendered HTML templates, no client-side JavaScript',
          '~2,300-line plain CSS design system on custom properties and color-mix()',
          'CSS-only menus/modals',
          'sharp for images',
          'Generated sitemap, JSON-LD event data, Open Graph',
          'HttpOnly cookie sessions',
        ],
      },
      {
        group: 'Backend',
        items: [
          'Node.js (ES modules) versioned /v1 API',
          'Supabase (Postgres, Auth, PostgREST via supabase-js)',
          'Two schemas (exposed + private)',
          'SQL migrations, row-level security, security-invoker functions',
          "JWT bearer auth with the guest's session token forwarded server to server",
          "Node's built-in test runner",
          'Server-Timing headers and structured logs',
        ],
      },
      {
        group: 'Infrastructure',
        items: [
          'Vercel (two projects, GitHub-connected, custom domain, serverless, Portland region)',
          'Supabase hosted Postgres',
          'GitHub branch-based production deploys',
          'Evaluated Vercel vs Railway',
        ],
      },
      {
        group: 'AI tooling',
        items: ['Claude (planning, PM)', 'Claude Code (frontend)', 'Cursor (backend)'],
      },
    ],
    built: [
      {
        heading: 'Product concepts I designed',
        items: [
          {
            title: 'Persistent knowledge vs. current outing context',
            text: 'A one-off choice never overwrites a lasting preference.',
          },
          {
            title: 'Behavioral learning with evidence',
            text: 'Views, clicks and purchases are recorded but not treated as proof of liking; only explicit like/dislike signals change the guest picture; conflicting signals are counted, not erased.',
          },
          {
            title: 'Shrinking effort over time',
            text: 'A visible understanding level lets Zoey ask fewer questions as she learns.',
          },
          {
            title: 'Multi-stop experiences',
            text: 'Date, ordered stops, times, estimated costs, dress compatibility, and a reason for each pick.',
          },
          {
            title: 'Future-ready booking',
            text: 'Draft-and-approve execution intents with no charging, so payments can be added without redesign.',
          },
        ],
      },
    ],
    results: {
      heading: 'Measured results',
      note: 'Speed figures were measured on small test-account samples.',
      items: [
        'Onboarding cut from 8 pages to 5 after a question audit.',
        'Median backend speedups (15 alternating samples): profile save 458→310 ms; behavior events 237→173 ms; plan creation 465→358 ms.',
        'Plans bound to a single night, with a nearby-nights suggestion when none fit.',
        '57 automated backend tests passing.',
      ],
    },
    skills: [
      'Product strategy',
      'Architecture decisions',
      'Directing AI engineers',
      'Security thinking (owner-only RLS, private fields stripped, service-role key kept off the frontend, forgeable-column audit)',
      'Data integrity (no invented data, published-only inventory, unknown cost ≠ zero)',
      'Performance diagnosis (found a Virginia function / Oregon database region mismatch and fixed it)',
      'Incident response (recovered source for a production service with no commit behind it, then connected GitHub)',
      'Release management',
      'Live testing and triage',
      'Cost and vendor awareness',
    ],
  },
];

export const getCaseStudy = (slug) => caseStudies.find((study) => study.slug === slug);

export const flagships = caseStudies.filter((study) => study.flagship);
export const alsoBuilding = caseStudies.filter((study) => study.alsoBuilding);

export const screenshotSrc = (slug, file) => `/images/case-studies/${slug}/${file}`;

// Legacy projects, listed under "More projects" on /projects. Each summary is the first sentence of the original copy.
export const moreProjects = [
  {
    title: 'ezDrinkBot AI Agent',
    summary: 'An AI-powered drink ordering agent that uses natural language processing to understand and process beverage orders.',
    icon: 'check',
    tags: ['AI', 'Node.js', 'React', 'LLM'],
    description:
      'An AI-powered drink ordering agent that uses natural language processing to understand and process beverage orders. Built with a Node.js backend, React frontend, and Supabase prisma database, this project showcases my ability to integrate large language models for practical applications.',
    highlights: [
      { title: 'Real-time Interaction', text: 'Uses advanced LLM modeling to create natural conversational flows' },
      { title: 'Production-Ready', text: 'Currently deployed in both staging and production environments' },
      { title: 'Automated Testing', text: 'Comprehensive test suite built with Playwright for reliable performance' },
    ],
    links: [
      { label: 'View GitHub', href: 'https://github.com/MrAfroTech/ezDrinkBot' },
      {
        label: 'Watch Demo',
        href: 'https://www.capcut.com/view/7475795578979959351?workspaceId=7474038373343313925',
      },
    ],
  },
  {
    title: 'Automated Visual Regression Testing',
    summary: 'A robust framework for detecting and tracking visual changes across application versions.',
    icon: 'file',
    tags: ['JavaScript', 'Playwright', 'Pixelmatch'],
    description:
      'A robust framework for detecting and tracking visual changes across application versions. Ensures pixel-perfect UI consistency and identifies unintended visual regressions before they reach production.',
    links: [{ label: 'GitHub', href: 'https://github.com/MrAfroTech/AutomatedVisualRegressionTesting' }],
  },
  {
    title: 'Quantitative Research',
    summary: 'Python-based tools for financial markets analysis and algorithmic trading.',
    icon: 'chart',
    tags: ['Python', 'Data Analysis', 'BART Model'],
    description:
      'Python-based tools for financial markets analysis and algorithmic trading. Implements data analysis, backtesting, model development, and signal generation using advanced libraries and AI models.',
    links: [{ label: 'GitHub', href: 'https://github.com/MrAfroTech/QuantResearch' }],
  },
  {
    title: 'Desktop Automation',
    summary:
      'Tools for automating desktop interactions, including window management and UI interaction, implemented using Playwright, Sikuli (image recognition-based GUI automation), and AppleScript.',
    icon: 'monitor',
    tags: ['Playwright', 'Sikuli', 'AppleScript'],
    description:
      'Tools for automating desktop interactions, including window management and UI interaction, implemented using Playwright, Sikuli (image recognition-based GUI automation), and AppleScript.',
    // Pre-existing link; it points at the visual regression repo. Flagged in the PR for review.
    links: [{ label: 'GitHub', href: 'https://github.com/MrAfroTech/AutomatedVisualRegressionTesting' }],
  },
];
